import { gql } from '@apollo/client';

/**
 * 802.1X — push / remove / compatibility operations for network profiles.
 *
 * Spec: docs/specs/19-optiedge-mdm/802.1x/frank/01-plan-auth-profile.md §A.5.
 *
 * Operation names are pinned to the v=3 wire surface set in Step 6:
 *   - `pushNetworkProfile` (was `pushEnterpriseWifiProfile`)
 *   - `removeNetworkProfile` (was `removeEnterpriseWifiProfile`)
 *   - `enterpriseWifiCompatibility` (unchanged — Phase A capability heartbeat)
 *
 * The `validateEnterpriseWifiProfile` mutation was removed in Step 3 — its
 * EapProfile counterpart `validateEapProfile` lives in `./eap-profile.ts`.
 *
 * Variable rename: `wifiInfoId` → `networkProfileId`. The value is still
 * a `WifiInfo._id` since the Mongo collection name stays `wifiInfos`;
 * `networkProfileId` is the forward-looking wire field name.
 */

export const PUSH_NETWORK_PROFILE = gql`
  mutation pushNetworkProfile($networkProfileId: ID!, $deviceIds: [ID!]!) {
    pushNetworkProfile(networkProfileId: $networkProfileId, deviceIds: $deviceIds) {
      batchId
      dispatched
      skipped {
        deviceId
        reason
      }
    }
  }
`;

export const REMOVE_NETWORK_PROFILE = gql`
  mutation removeNetworkProfile($networkProfileId: ID!, $deviceIds: [ID!]!) {
    removeNetworkProfile(networkProfileId: $networkProfileId, deviceIds: $deviceIds) {
      batchId
      dispatched
      skipped {
        deviceId
        reason
      }
    }
  }
`;

// `authentication` is the EAP method of the profile about to be pushed. The
// server applies the method gate with it (EAP-TTLS excludes Android until Utils
// supports it); omitted, the chips are method-agnostic as before.
export const ENTERPRISE_WIFI_COMPATIBILITY = gql`
  query EnterpriseWifiCompatibility($deviceIds: [String!]!, $authentication: AUTHENTICATION_TYPES) {
    enterpriseWifiCompatibility(deviceIds: $deviceIds, authentication: $authentication) {
      deviceId
      supported
      reason
      capabilities {
        tls13
        wpa3
        wpa3_192bit
        strongbox
      }
    }
  }
`;

// Mutation result + variable types — feature consumers import these
export interface NetworkProfilePushPayload {
  batchId: string;
  dispatched: number;
  skipped: Array<{ deviceId: string; reason: string }>;
}

export interface PushNetworkProfileResult {
  pushNetworkProfile: NetworkProfilePushPayload;
}

export interface RemoveNetworkProfileResult {
  removeNetworkProfile: NetworkProfilePushPayload;
}

export interface PushNetworkProfileVariables {
  networkProfileId: string;
  deviceIds: string[];
}

export interface EnterpriseWifiCompatibilityResult {
  enterpriseWifiCompatibility: Array<{
    deviceId: string;
    supported: boolean;
    reason: string | null;
    capabilities: {
      tls13: boolean;
      wpa3: boolean;
      wpa3_192bit: boolean;
      strongbox: boolean;
    };
  }>;
}

export interface EnterpriseWifiCompatibilityVariables {
  deviceIds: string[];
  /** GraphQL enum name of the profile's EAP method (`PEAP` | `EAP_TTLS` | `EAP_TLS`). */
  authentication?: string;
}

/**
 * Resolve a fleet / tag / search filter to the devices a push would reach.
 *
 * Returns ids and counts only — no device documents. This is what lets the push
 * dialog offer "select all 126 eligible" without first paging the client
 * through an 800-device fleet, and what makes its counts trustworthy: the
 * server runs the same eligibility gate the push itself runs.
 */
export const NETWORK_PROFILE_TARGETS = gql`
  query NetworkProfileTargets($filter: NetworkProfileTargetFilterInput) {
    networkProfileTargets(filter: $filter) {
      eligibleDeviceIds
      eligibleCount
      matchedCount
    }
  }
`;

export interface NetworkProfileTargetFilter {
  teamId?: string;
  /** Specific fleet. Ignored when `fleetIsDefault` is true. */
  fleetId?: string;
  /** The virtual Default fleet — `fleetId` null OR absent. */
  fleetIsDefault?: boolean;
  /** Match devices carrying ANY of these tags. */
  tags?: string[];
  /** Case-insensitive substring match on deviceName. */
  search?: string;
  /**
   * The network profile the push is for. Lets the server apply the EAP-method
   * gate (an EAP-TTLS profile excludes Android) to the counts and ids it
   * returns, so "126 eligible" already means eligible for THIS profile.
   */
  networkProfileId?: string;
}

export interface NetworkProfileTargetsResult {
  networkProfileTargets: {
    /** Ids that pass the eligibility gate — safe to hand to pushNetworkProfile. */
    eligibleDeviceIds: string[];
    eligibleCount: number;
    /** Devices matching the filter, eligible or not — the "of 800" denominator. */
    matchedCount: number;
  };
}
